import {
  AuditUseCase,
  AuditTask,
  AuditResult,
  TASK_STATUS,
  RESULT_STATUS,
  UseCaseConfig,
  USE_CASE_CATEGORY,
  DATA_SOURCE_TYPE,
  COMPARISON_MODE,
  FIELD_TYPE,
  makeReviewSampling,
  WorkspaceTask,
  WorkspaceResult,
} from "./types";

// Placeholder alias used in sample data. Thunks replace this with the
// logged-in user so isMine() matches, while preserving other reviewer IDs.
export const SAMPLE_AUDITOR_ALIAS = "sample-auditor";

// TODO: Remove all sample data once audit v2 migration is done
export const SAMPLE_USE_CASES: AuditUseCase[] = [
  { id: "uc-safety-genie", name: "Safety Genie – Q&A Accuracy", inter_rater_count: 1 },
  { id: "uc-safety-vision", name: "Safety Vision – PPE Detection", inter_rater_count: 2 },
  { id: "uc-custom-ergo", name: "Custom – Ergo Risk Scoring", inter_rater_count: 1 },
];

export const SAMPLE_TASKS: AuditTask[] = [
  // Safety Genie (5): 3 claimed, 1 completed, 1 available
  {
    id: "task-sg-001",
    use_case_id: "uc-safety-genie",
    status: TASK_STATUS.CLAIMED,
    assigned_to: "sample-auditor",
    priority: 2,
    created_at: "2026-01-10T08:00:00Z",
  },
  {
    id: "task-sg-002",
    use_case_id: "uc-safety-genie",
    status: TASK_STATUS.CLAIMED,
    assigned_to: "sample-auditor",
    priority: 1,
    created_at: "2026-01-11T14:00:00Z",
  },
  {
    id: "task-sg-003",
    use_case_id: "uc-safety-genie",
    status: TASK_STATUS.CLAIMED,
    assigned_to: "sample-auditor",
    priority: 3,
    created_at: "2026-01-12T06:30:00Z",
  },
  {
    id: "task-sg-004",
    use_case_id: "uc-safety-genie",
    status: TASK_STATUS.COMPLETED,
    assigned_to: "sample-auditor",
    priority: 0,
    created_at: "2026-01-08T14:00:00Z",
  },
  {
    id: "task-sg-005",
    use_case_id: "uc-safety-genie",
    status: TASK_STATUS.AVAILABLE,
    assigned_to: "sample-auditor",
    priority: 1,
    created_at: "2026-01-13T07:00:00Z",
  },
  // Safety Vision (3): 2 claimed, 1 available
  {
    id: "task-sv-001",
    use_case_id: "uc-safety-vision",
    status: TASK_STATUS.CLAIMED,
    assigned_to: "sample-auditor",
    priority: 1,
    created_at: "2026-01-11T10:30:00Z",
  },
  {
    id: "task-sv-002",
    use_case_id: "uc-safety-vision",
    status: TASK_STATUS.CLAIMED,
    assigned_to: "sample-auditor",
    priority: 2,
    created_at: "2026-01-12T09:00:00Z",
  },
  {
    id: "task-sv-003",
    use_case_id: "uc-safety-vision",
    status: TASK_STATUS.AVAILABLE,
    assigned_to: "sample-auditor",
    priority: 3,
    created_at: "2026-01-13T08:15:00Z",
  },
  // Ergo (2): 1 claimed, 1 expired
  {
    id: "task-ergo-001",
    use_case_id: "uc-custom-ergo",
    status: TASK_STATUS.CLAIMED,
    assigned_to: "sample-auditor",
    priority: 3,
    created_at: "2026-01-12T07:00:00Z",
  },
  {
    id: "task-ergo-002",
    use_case_id: "uc-custom-ergo",
    status: TASK_STATUS.EXPIRED,
    assigned_to: "sample-auditor",
    priority: 0,
    created_at: "2026-01-01T12:00:00Z",
  },
];

export const SAMPLE_RESULTS: AuditResult[] = [
  {
    id: "res-001",
    task_id: "task-sg-004",
    auditor_id: "sample-auditor",
    status: RESULT_STATUS.SUBMITTED,
    decisions: { accuracy: { decision: "correct" }, completeness: { decision: "correct" } },
  },
  {
    id: "res-002",
    task_id: "task-ergo-002",
    auditor_id: "sample-auditor",
    status: RESULT_STATUS.SUBMITTED,
    decisions: { bbox_quality: { decision: "correct" }, label_match: { decision: "incorrect" } },
  },
  {
    id: "res-003",
    task_id: "task-ergo-002",
    auditor_id: "reviewer02",
    status: RESULT_STATUS.SUBMITTED,
    decisions: { bbox_quality: { decision: "correct" }, label_match: { decision: "correct" } },
  },
  {
    id: "res-004",
    task_id: "task-sv-001",
    auditor_id: "sample-auditor",
    status: RESULT_STATUS.DRAFT,
    decisions: {},
  },
];

// ----------------------------  Sample Config Data  ----------------------------

export const SAMPLE_USE_CASE_CONFIGS: UseCaseConfig[] = [
  {
    id: "uc-safety-genie",
    name: "Safety Genie - Q&A Accuracy",
    description: "Evaluate Safety Genie chatbot answers for accuracy and completeness.",
    category: USE_CASE_CATEGORY.SAFETY_GENIE,
    data_source_type: DATA_SOURCE_TYPE.TEXT_ONLY,
    comparison_mode: COMPARISON_MODE.SIMPLE,
    inter_rater_count: 1,
    review_sampling: makeReviewSampling(),
    claim_expiry_minutes: 60,
    status: "active",
    fields: [
      {
        id: "f-sg-question",
        type: FIELD_TYPE.TEXT_INPUT,
        label: "Question",
        options: [],
        mandatory: true,
        help_text: "The original user question sent to Safety Genie.",
        display_order: 0,
        default_value: "",
        multiline: true,
        char_limit: null,
        min: null,
        max: null,
        step: null,
        allow_other: false,
      },
      {
        id: "f-sg-answer",
        type: FIELD_TYPE.TEXT_INPUT,
        label: "AI Answer",
        options: [],
        mandatory: true,
        help_text: "The answer generated by Safety Genie.",
        display_order: 1,
        default_value: "",
        multiline: true,
        char_limit: null,
        min: null,
        max: null,
        step: null,
        allow_other: false,
      },
      {
        id: "f-sg-accuracy",
        type: FIELD_TYPE.DROPDOWN_SINGLE,
        label: "Accuracy",
        options: ["Correct", "Partially Correct", "Incorrect"],
        mandatory: true,
        help_text: "Rate the factual accuracy of the answer.",
        display_order: 2,
        default_value: "",
        multiline: false,
        char_limit: null,
        min: null,
        max: null,
        step: null,
        allow_other: false,
      },
    ],
    checklist_items: [
      { id: "cl-sg-1", label: "Answer addresses the question", display_order: 0 },
      { id: "cl-sg-2", label: "No hallucinated facts", display_order: 1 },
      { id: "cl-sg-3", label: "Proper citation of sources", display_order: 2 },
    ],
    created_at: "2026-01-05T10:00:00Z",
    updated_at: "2026-01-05T10:00:00Z",
  },
  {
    id: "uc-safety-vision",
    name: "Safety Vision - PPE Detection",
    description: "Validate PPE detection model outputs on warehouse images.",
    category: USE_CASE_CATEGORY.SAFETY_VISION,
    data_source_type: DATA_SOURCE_TYPE.SINGLE_IMAGE_TEXT,
    comparison_mode: COMPARISON_MODE.OVERRIDE,
    inter_rater_count: 2,
    review_sampling: makeReviewSampling({
      second_pct: 10,
      second_enabled: true,
      second_reviewer: "auditor02",
    }),
    claim_expiry_minutes: 120,
    status: "active",
    fields: [
      {
        id: "f-sv-image",
        type: FIELD_TYPE.IMAGE_VIEWER,
        label: "Source Image",
        options: [],
        mandatory: true,
        help_text: "The warehouse camera image.",
        display_order: 0,
        default_value: "",
        multiline: false,
        char_limit: null,
        min: null,
        max: null,
        step: null,
        allow_other: false,
      },
      {
        id: "f-sv-ppe-type",
        type: FIELD_TYPE.DROPDOWN_MULTI,
        label: "PPE Detected",
        options: ["Hard Hat", "Safety Vest", "Gloves", "Safety Glasses"],
        mandatory: true,
        help_text: "Select all PPE types detected in the image.",
        display_order: 1,
        default_value: "",
        multiline: false,
        char_limit: null,
        min: null,
        max: null,
        step: null,
        allow_other: true,
      },
    ],
    checklist_items: [
      { id: "cl-sv-1", label: "Bounding boxes are accurate", display_order: 0 },
      { id: "cl-sv-2", label: "All PPE items identified", display_order: 1 },
    ],
    created_at: "2026-01-06T08:00:00Z",
    updated_at: "2026-01-06T08:00:00Z",
  },
  {
    id: "uc-custom-ergo",
    name: "Custom - Ergo Risk Scoring",
    description: "Review ergonomic risk scoring results.",
    category: USE_CASE_CATEGORY.CUSTOM,
    data_source_type: DATA_SOURCE_TYPE.VIDEO_TEXT,
    comparison_mode: COMPARISON_MODE.SIMPLE,
    inter_rater_count: 1,
    review_sampling: makeReviewSampling(),
    claim_expiry_minutes: 240,
    status: "inactive",
    fields: [
      {
        id: "f-ergo-video",
        type: FIELD_TYPE.VIDEO_PLAYER,
        label: "Task Video",
        options: [],
        mandatory: true,
        help_text: "Video of the associate performing the task.",
        display_order: 0,
        default_value: "",
        multiline: false,
        char_limit: null,
        min: null,
        max: null,
        step: null,
        allow_other: false,
      },
      {
        id: "f-ergo-score",
        type: FIELD_TYPE.NUMERIC,
        label: "Risk Score",
        options: [],
        mandatory: true,
        help_text: "Numeric risk score (1-10).",
        display_order: 1,
        default_value: "",
        multiline: false,
        char_limit: null,
        min: 1,
        max: 10,
        step: 1,
        allow_other: false,
      },
    ],
    checklist_items: [{ id: "cl-ergo-1", label: "Posture evaluated correctly", display_order: 0 }],
    created_at: "2026-01-07T12:00:00Z",
    updated_at: "2026-01-07T12:00:00Z",
  },
];

// ----------------------------  Sample Workspace Data  ----------------------------

export const SAMPLE_WORKSPACE_TASKS: WorkspaceTask[] = [
  // ---  Safety Genie – Q&A Accuracy (5 tasks)  ---
  {
    id: "task-sg-001",
    use_case_id: "uc-safety-genie",
    status: TASK_STATUS.CLAIMED,
    assigned_to: SAMPLE_AUDITOR_ALIAS,
    priority: 2,
    created_at: "2026-01-10T08:00:00Z",
    week: "2026-W02",
    incident_description:
      "Associate reported a near-miss involving a forklift in aisle 14. The Safety Genie system generated a Q&A summary of the incident that needs accuracy review.",
    comparison_mode: "simple",
    ai_generated_values: {
      Question: "What happened in the near-miss incident in aisle 14?",
      "AI Answer":
        "A forklift operator turned into aisle 14 without checking for pedestrian traffic. An associate had to step back quickly to avoid contact.",
      Accuracy: "Correct",
    },
    media_urls: [],
  },
  {
    id: "task-sg-002",
    use_case_id: "uc-safety-genie",
    status: TASK_STATUS.CLAIMED,
    assigned_to: SAMPLE_AUDITOR_ALIAS,
    priority: 1,
    created_at: "2026-01-11T14:00:00Z",
    week: "2026-W02",
    incident_description:
      "An associate slipped on a wet floor near the break room. Safety Genie produced a root-cause summary that needs review.",
    comparison_mode: "simple",
    ai_generated_values: {
      Question: "What caused the slip incident near the break room?",
      "AI Answer":
        "The floor had been mopped 10 minutes prior without a wet-floor sign. The associate was wearing non-slip shoes but still lost traction.",
      Accuracy: "Partially Correct",
    },
    media_urls: [],
  },
  {
    id: "task-sg-003",
    use_case_id: "uc-safety-genie",
    status: TASK_STATUS.CLAIMED,
    assigned_to: SAMPLE_AUDITOR_ALIAS,
    priority: 3,
    created_at: "2026-01-12T06:30:00Z",
    week: "2026-W02",
    incident_description:
      "Roll cage at Dock 12 photographed during the morning safety sweep. VLM flagged no-smoking and authorized-entry signage as present, but hazardous-waste storage labeling appears missing. Review the Q&A summary.",
    comparison_mode: "simple",
    ai_generated_values: {
      Question: "What compliance issues were found at Dock 12 roll cage?",
      "AI Answer":
        "No-smoking and authorized-entry signage were present. Hazardous-waste storage labeling was missing. Fire extinguisher bracket was visible but unit may be absent.",
      Accuracy: "Correct",
    },
    media_urls: [],
  },
  {
    id: "task-sg-004",
    use_case_id: "uc-safety-genie",
    status: TASK_STATUS.CLAIMED,
    assigned_to: SAMPLE_AUDITOR_ALIAS,
    priority: 2,
    created_at: "2026-01-12T11:00:00Z",
    week: "2026-W02",
    incident_description:
      "A floor supervisor overrode several VLM outputs during the initial pass at Sort Center Zone B. Safety Genie summarized the discrepancies. Verify the AI summary is accurate.",
    comparison_mode: "simple",
    ai_generated_values: {
      Question: "What discrepancies were found between VLM and supervisor at Zone B?",
      "AI Answer":
        "The supervisor corrected no-smoking signage from No to Yes and hazardous-waste storage from Yes to No. All other values matched.",
      Accuracy: "Correct",
    },
    media_urls: [],
  },
  {
    id: "task-sg-005",
    use_case_id: "uc-safety-genie",
    status: TASK_STATUS.CLAIMED,
    assigned_to: SAMPLE_AUDITOR_ALIAS,
    priority: 1,
    created_at: "2026-01-13T07:00:00Z",
    week: "2026-W02",
    incident_description:
      "Associate reported a box fell from an overhead rack in aisle 9 during restocking. Safety Genie generated a root-cause analysis. Confirm the AI answer is factually accurate.",
    comparison_mode: "simple",
    ai_generated_values: {
      Question: "What caused the falling box incident in aisle 9?",
      "AI Answer":
        "The box was placed beyond the rack lip without a restraint strap. Vibration from a nearby PIT caused it to slide off during restocking of the adjacent bay.",
      Accuracy: "Partially Correct",
    },
    media_urls: [],
  },
  // ---  Safety Vision – PPE Detection (3 tasks)  ---
  {
    id: "task-sv-001",
    use_case_id: "uc-safety-vision",
    status: TASK_STATUS.CLAIMED,
    assigned_to: SAMPLE_AUDITOR_ALIAS,
    priority: 1,
    created_at: "2026-01-11T10:30:00Z",
    week: "2026-W02",
    incident_description:
      "Dock loading scene captured by the CV model. Four PPE/zone detections were generated. Review each detection and mark it correct, incorrect, or partially correct.",
    comparison_mode: "override",
    ai_generated_values: {
      "PPE Detected": "Hard Hat, Safety Glasses",
    },
    human_override_values: {
      "PPE Detected": "Hard Hat, Safety Vest, Safety Glasses",
    },
    media_urls: [
      {
        url: "https://picsum.photos/seed/vision-scene/800/500",
        type: "image",
        label: "Dock Scene",
      },
    ],
  },
  {
    id: "task-sv-002",
    use_case_id: "uc-safety-vision",
    status: TASK_STATUS.CLAIMED,
    assigned_to: SAMPLE_AUDITOR_ALIAS,
    priority: 2,
    created_at: "2026-01-12T09:00:00Z",
    week: "2026-W02",
    incident_description:
      "Safety Vision flagged an associate near the conveyor belt without safety gloves. Verify the detection output against the camera image.",
    comparison_mode: "override",
    ai_generated_values: {
      "PPE Detected": "Safety Vest",
    },
    human_override_values: {
      "PPE Detected": "Safety Vest, Gloves",
    },
    media_urls: [
      {
        url: "https://picsum.photos/seed/conveyor4/800/500",
        type: "image",
        label: "Conveyor Camera 004",
      },
    ],
  },
  {
    id: "task-sv-003",
    use_case_id: "uc-safety-vision",
    status: TASK_STATUS.CLAIMED,
    assigned_to: SAMPLE_AUDITOR_ALIAS,
    priority: 3,
    created_at: "2026-01-13T08:15:00Z",
    week: "2026-W02",
    incident_description:
      "Warehouse aisle video clip with per-frame model detections. Step through the frames, review each detection, and adjust where the model is wrong.",
    comparison_mode: "override",
    ai_generated_values: {
      "PPE Detected": "Hard Hat",
    },
    human_override_values: {
      "PPE Detected": "Hard Hat, Safety Vest, Gloves",
    },
    media_urls: [
      {
        url: "https://picsum.photos/seed/aisle-clip/800/500",
        type: "image",
        label: "Warehouse Aisle",
      },
    ],
  },
  // ---  Custom – Ergo Risk Scoring (2 tasks)  ---
  {
    id: "task-ergo-001",
    use_case_id: "uc-custom-ergo",
    status: TASK_STATUS.CLAIMED,
    assigned_to: SAMPLE_AUDITOR_ALIAS,
    priority: 3,
    created_at: "2026-01-12T07:00:00Z",
    week: "2026-W02",
    incident_description:
      "Ergo risk model scored a repetitive lifting task at station B7 as high-risk. Review the video and model output for accuracy.",
    comparison_mode: "simple",
    ai_generated_values: {
      "Risk Score": "High",
      "Body Region": "Lower Back",
    },
    human_override_values: {},
    media_urls: [
      { url: "/sample/ergo-station-b7.mp4", type: "video", label: "Station B7 Task Video" },
    ],
  },
  {
    id: "task-ergo-002",
    use_case_id: "uc-custom-ergo",
    status: TASK_STATUS.CLAIMED,
    assigned_to: SAMPLE_AUDITOR_ALIAS,
    priority: 2,
    created_at: "2026-01-13T06:30:00Z",
    week: "2026-W02",
    incident_description:
      "Ergo model flagged overhead reaching at packing station C3 as medium-risk. Validate the risk score and body region assessment.",
    comparison_mode: "simple",
    ai_generated_values: {
      "Risk Score": "Medium",
      "Body Region": "Shoulder",
    },
    human_override_values: {},
    media_urls: [
      { url: "/sample/ergo-station-c3.mp4", type: "video", label: "Station C3 Task Video" },
    ],
  },
];

export const SAMPLE_WORKSPACE_RESULTS: WorkspaceResult[] = [
  {
    id: "wres-001",
    task_id: "task-sg-001",
    auditor_id: SAMPLE_AUDITOR_ALIAS,
    status: "draft",
    decisions: {
      Question: { decision: "correct" },
    },
    checklist_responses: {
      "cl-sg-1": "pass",
    },
    comments: "",
  },
];
